import React, { useState, useEffect } from 'react';
import { Plus, CircleArrowRight, X } from 'lucide-react';
import { token_sort_ratio } from 'fuzzball';
import { toast } from 'react-toastify';
import SchemaPair from './SchemaPair';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import Tooltip from '../../components/common/Tooltip/Tooltip';

// ─── Constants ────────────────────────────────────────────────────────────────

const FUZZY_THRESHOLD = 90;

const TABS = {
    AUTOMATED: 'automated',
    MANUAL: 'manual',
};

const initialSchemaPair = {
    sourceSchema: '',
    targetSchema: '',
    addFilter: false,
    filterExpanded: false,
    includePrefix: [],
    includeSuffix: [],
    excludePrefix: [],
    excludeSuffix: [],
};

const initialGlobalFilter_default = {
    enabled: false,
    includePrefix: [],
    includeSuffix: [],
    excludePrefix: [],
    excludeSuffix: [],
};

// ─── Fuzzy matching (token-based Levenshtein, mirrors fuzzball token_set_ratio) ─

// function levenshtein(a, b) {
//     const m = a.length, n = b.length;
//     const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
//     for (let j = 0; j <= n; j++) dp[0][j] = j;
//     for (let i = 1; i <= m; i++) {
//         for (let j = 1; j <= n; j++) {
//             dp[i][j] = a[i - 1] === b[j - 1]
//                 ? dp[i - 1][j - 1]
//                 : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
//         }
//     }
//     return dp[m][n];
// }

// function tokenSetRatio(s1, s2) {
//     const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, ' ').trim();
//     const tokens1 = new Set(normalize(s1).split(/\s+/).filter(Boolean));
//     const tokens2 = new Set(normalize(s2).split(/\s+/).filter(Boolean));
//     const intersection = [...tokens1].filter(t => tokens2.has(t)).sort().join(' ');
//     const sorted1 = [...tokens1].sort().join(' ');
//     const sorted2 = [...tokens2].sort().join(' ');
//     const ratio = (a, b) => {
//         const maxLen = Math.max(a.length, b.length);
//         if (maxLen === 0) return 100;
//         return Math.round((1 - levenshtein(a, b) / maxLen) * 100);
//     };
//     return Math.max(
//         ratio(intersection, sorted1),
//         ratio(intersection, sorted2),
//         ratio(sorted1, sorted2),
//     );
// }

const buildFuzzyPairs = (sourceSchemas = [], targetSchemas = []) => {
    const pairs = [];
    const matchedTargets = new Set();
    const matchedSources = new Set();

    // --------------------------------
    // Phase 1: Exact matches (priority)
    // --------------------------------
    sourceSchemas.forEach((source) => {
        if (targetSchemas.includes(source)) {
            pairs.push({
                ...initialSchemaPair,
                sourceSchema: source,
                targetSchema: source
            });

            matchedSources.add(source);
            matchedTargets.add(source);
        }
    });

    // ----------------------------------------
    // Phase 2: Fuzzy match remaining (controlled)
    // ----------------------------------------
    sourceSchemas.forEach((source) => {
        if (matchedSources.has(source)) return;

        let bestMatch = "";
        let bestScore = 0;

        targetSchemas.forEach((target) => {
            if (matchedTargets.has(target)) return;

            const score = token_sort_ratio(source, target);

            if (score >= FUZZY_THRESHOLD && score > bestScore) {
                bestMatch = target;
                bestScore = score;
            }
        });

        if (bestMatch) {
            matchedTargets.add(bestMatch);

            pairs.push({
                ...initialSchemaPair,
                sourceSchema: source,
                targetSchema: bestMatch
            });
        } else {
            pairs.push({
                ...initialSchemaPair,
                sourceSchema: source,
                targetSchema: ""
            });
        }
    });

    // Unmatched targets
    targetSchemas.forEach(target => {
        if (!matchedTargets.has(target)) {
            pairs.push({
                ...initialSchemaPair,
                sourceSchema: '',
                targetSchema: target
            });
        }
    });

    return pairs;
};

// ─── Component ────────────────────────────────────────────────────────────────

const SchemaComparisons = ({
    schemas,
    loadingSchemas,
    schemasAvailable,
    connectionsReady,
    errors,
    setErrors,
    onPairsChange,      // (pairs, isAutomated, globalFilter) => void
    initialTab,         // 'automated' | 'manual' — optional, for edit mode
    initialPairs,       // pre-populated pairs — if provided, skip fuzzy matching
    initialGlobalFilter, // pre-populated global filter — for edit mode automated jobs
    modalBodyRef,
}) => {
    const [activeTab, setActiveTab] = useState(initialTab === TABS.MANUAL ? TABS.MANUAL : TABS.AUTOMATED);

    // Automated tab state
    const [autoPairs, setAutoPairs] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter || initialGlobalFilter_default);
    const [globalInputStates, setGlobalInputStates] = useState({});
    const [autoInputStates, setAutoInputStates] = useState({});

    // Manual tab state
    const [manualPairs, setManualPairs] = useState([{ ...initialSchemaPair }]);
    const [manualInputStates, setManualInputStates] = useState({});

    // ─── Seed state from initialPairs / initialGlobalFilter (edit mode) ────────

    useEffect(() => {
        if (initialPairs) {
            // Edit mode: use provided pairs directly, skip fuzzy matching
            if (initialTab === TABS.AUTOMATED) {
                setAutoPairs(initialPairs);
            } else {
                setManualPairs(initialPairs.length > 0 ? initialPairs : [{ ...initialSchemaPair }]);
            }
        }
    }, [initialPairs]);

    useEffect(() => {
        if (initialGlobalFilter) {
            setGlobalFilter(initialGlobalFilter);
        }
    }, [initialGlobalFilter]);

    // ─── Build fuzzy pairs whenever schemas change (add mode only) ────────────

    useEffect(() => {
        // Skip fuzzy matching if we were given pre-populated pairs (edit mode)
        if (initialPairs) return;
        // if (activeTab !== TABS.AUTOMATED) return;
        // if (autoPairs.length > 0) return;

        if (schemas.source.length > 0 || schemas.target.length > 0) {
            const pairs = buildFuzzyPairs(schemas.source, schemas.target);
            setAutoPairs(pairs);
        } else {
            setAutoPairs([]);
        }

        // Reset global filter when schemas reload
        setGlobalFilter(initialGlobalFilter_default);
        setGlobalInputStates({});
        setAutoInputStates({});
        // setAutoPairs([]);
        // setManualPairs([]);
    }, [schemas.source, schemas.target]);

    // ─── Notify parent whenever active pairs change ────────────────────────────

    useEffect(() => {
        const activePairs = activeTab === TABS.AUTOMATED ? autoPairs : manualPairs;
        onPairsChange(activePairs, activeTab === TABS.AUTOMATED, globalFilter);
    }, [activeTab, autoPairs, manualPairs, globalFilter]);

    // ─── Tab switch ───────────────────────────────────────────────────────────

    const handleTabChange = (tab) => {
        setActiveTab(tab);

        setErrors(prev => {
            const next = { ...prev };

            Object.keys(next).forEach(key => {
                if (
                    key.startsWith('sourceSchema_') ||
                    key.startsWith('targetSchema_') ||
                    key.startsWith('filter_')
                ) {
                    delete next[key];
                }
            });

            delete next['globalFilter'];

            return next;
        });

        if (
            tab === TABS.AUTOMATED &&
            autoPairs.length === 0 &&
            (schemas.source.length > 0 || schemas.target.length > 0)
        ) {
            const pairs = buildFuzzyPairs(schemas.source, schemas.target);
            setAutoPairs(pairs);
        }
    };

    // ─── Shared chip logic (parameterised by setter + inputStates setter) ─────

    const makeAddChip = (setPairs, setInputs, getPairs) => (pairIndex, filterType, inputKey) => {
        const currentInputStates = getPairs(); // closure over latest inputStates
        const rawInput = currentInputStates[inputKey];
        if (!rawInput) return;

        const newChips = rawInput
            .split(',')
            .map(v => v.replace(/^,+|,+$/g, ''))
            .filter(v => v.trim() !== '');

        if (newChips.length === 0) return;

        const oversized = newChips.find(chip => chip.length > 20);
        if (oversized) { toast.error(`'${oversized}' exceeds the 20 character limit`); return; }

        const uniqueNew = newChips.filter((c, i) => newChips.indexOf(c) === i);

        setPairs(prev => {
            const existing = prev[pairIndex][filterType];
            const dups = uniqueNew.filter(c => existing.includes(c));
            if (dups.length > 0) { toast.error(`'${dups.join("', '")}' already exists`); return prev; }
            const updated = [...prev];
            updated[pairIndex] = {
                ...updated[pairIndex],
                [filterType]: [...uniqueNew.reverse(), ...existing],
            };
            return updated;
        });

        setErrors(prev => {
            const next = { ...prev };
            delete next[`filter_${pairIndex}`];
            return next;
        });
        setInputs(prev => ({ ...prev, [inputKey]: '' }));

    };

    const makeRemoveChip = (setPairs) => (pairIndex, filterType, chipIndex) => {
        setPairs(prev => {
            const updated = [...prev];
            updated[pairIndex] = {
                ...updated[pairIndex],
                [filterType]: updated[pairIndex][filterType].filter((_, i) => i !== chipIndex),
            };
            return updated;
        });
    };

    const makeHandleInputChange = (setInputs) => (inputKey, value) => {
        setInputs(prev => ({ ...prev, [inputKey]: value }));
    };

    const makeHandleKeyPress = (addChip) => (e, pairIndex, filterType, inputKey) => {
        if (e.key === 'Enter') { e.preventDefault(); addChip(pairIndex, filterType, inputKey); }
    };

    // ─── Automated tab handlers ───────────────────────────────────────────────

    const autoAddChip = makeAddChip(setAutoPairs, setAutoInputStates, () => autoInputStates);
    const autoRemoveChip = makeRemoveChip(setAutoPairs);
    const autoInputChange = makeHandleInputChange(setAutoInputStates);
    const autoKeyPress = makeHandleKeyPress(autoAddChip);

    const handleAutoPairChange = (index, field, value) => {
        setAutoPairs(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
        if (field === 'sourceSchema' || field === 'targetSchema') {
            setErrors(prev => {
                const next = { ...prev };
                delete next[`${field}_${index}`];
                return next;
            });
        }
    };

    const handleAutoRemovePair = (index) => {
        setAutoPairs(prev => prev.filter((_, i) => i !== index));
        setAutoInputStates(prev => {
            const next = { ...prev };
            ['includePrefix', 'includeSuffix', 'excludePrefix', 'excludeSuffix'].forEach(k => {
                delete next[`${index}-${k}`];
            });
            return next;
        });
        // Re-index error keys: remove errors for deleted pair, shift down errors for pairs after it
        setErrors(prev => {
            const next = {};
            Object.keys(prev).forEach(key => {
                const match = key.match(/^(sourceSchema_|targetSchema_|filter_)(\d+)$/);
                if (match) {
                    const i = parseInt(match[2], 10);
                    if (i === index) return; // drop errors for removed pair
                    const newKey = `${match[1]}${i > index ? i - 1 : i}`;
                    next[newKey] = prev[key];
                } else {
                    next[key] = prev[key];
                }
            });
            return next;
        });
    };

    const handleAutoToggleFilter = (index) => {
        setAutoPairs(prev => {
            const updated = [...prev];
            const enabling = !updated[index].addFilter;
            if (!enabling) {
                updated[index] = {
                    ...updated[index],
                    addFilter: false,
                    filterExpanded: false,
                    includePrefix: [],
                    includeSuffix: [],
                    excludePrefix: [],
                    excludeSuffix: [],
                };
                setAutoInputStates(prev => {
                    const next = { ...prev };
                    ['includePrefix', 'includeSuffix', 'excludePrefix', 'excludeSuffix'].forEach(k => {
                        delete next[`${index}-${k}`];
                    });
                    return next;
                });
                setErrors(prev => {
                    const next = { ...prev };
                    delete next[`filter_${index}`];
                    return next;
                });
            } else {
                updated[index] = { ...updated[index], addFilter: true, filterExpanded: true };
            }
            return updated;
        });
    };

    const handleAddAutoPair = () => {
        setAutoPairs(prev => [...prev, { ...initialSchemaPair }]);
    };

    // ─── Global filter handlers ───────────────────────────────────────────────

    const handleGlobalChipAdd = (filterType, inputKey) => {
        const rawInput = globalInputStates[inputKey];
        if (!rawInput) return;

        const newChips = rawInput
            .split(',')
            .map(v => v.replace(/^,+|,+$/g, ''))
            .filter(v => v.trim() !== '');

        if (newChips.length === 0) return;

        const oversized = newChips.find(chip => chip.length > 20);
        if (oversized) { toast.error(`'${oversized}' exceeds the 20 character limit`); return; }

        const uniqueNew = newChips.filter((c, i) => newChips.indexOf(c) === i);
        const existing = globalFilter[filterType];
        const dups = uniqueNew.filter(c => existing.includes(c));
        if (dups.length > 0) { toast.error(`'${dups.join("', '")}' already exists`); return; }

        setGlobalFilter(prev => {
            const updatedFilter = {
                ...prev,
                [filterType]: [...uniqueNew.reverse(), ...prev[filterType]],
            };
            // Live-push this filterType to all auto pairs immediately
            setAutoPairs(pairs => pairs.map(pair => ({
                ...pair,
                addFilter: true,
                // filterExpanded: false,
                // [filterType]: [...updatedFilter[filterType]],
                includePrefix: [...updatedFilter.includePrefix],
                includeSuffix: [...updatedFilter.includeSuffix],
                excludePrefix: [...updatedFilter.excludePrefix],
                excludeSuffix: [...updatedFilter.excludeSuffix],
            })));
            return updatedFilter;
        });

        setErrors(prev => {
            const next = { ...prev };
            delete next['globalFilter'];
            Object.keys(next).forEach(key => {
                if (key.startsWith('filter_')) delete next[key];
            });
            return next;
        });

        setGlobalInputStates(prev => ({ ...prev, [inputKey]: '' }));
    };

    const handleGlobalChipRemove = (filterType, chipIndex) => {
        setGlobalFilter(prev => {
            const updatedFilter = {
                ...prev,
                [filterType]: prev[filterType].filter((_, i) => i !== chipIndex),
            }
            // Live-push this filterType to all auto pairs immediately
            setAutoPairs(pairs => pairs.map(pair => ({
                ...pair,
                addFilter: true,
                // filterExpanded: false,
                // [filterType]: [...updatedFilter[filterType]],
                includePrefix: [...updatedFilter.includePrefix],
                includeSuffix: [...updatedFilter.includeSuffix],
                excludePrefix: [...updatedFilter.excludePrefix],
                excludeSuffix: [...updatedFilter.excludeSuffix],
            })));
            return updatedFilter;
        })
    };

    const handleGlobalToggleFilter = () => {
        setGlobalFilter(prev => {
            const enabling = !prev.enabled;
            if (!enabling) {
                setGlobalInputStates({});
                setAutoPairs(pairs => pairs.map(pair => ({
                    ...pair,
                    addFilter: false,
                    filterExpanded: false,
                    includePrefix: [],
                    includeSuffix: [],
                    excludePrefix: [],
                    excludeSuffix: [],
                })));

                setErrors(prev => {
                    const next = { ...prev };
                    delete next['globalFilter'];
                    Object.keys(next).forEach(key => {
                        if (key.startsWith('filter_')) delete next[key];
                    });
                    return next;
                });
                return { ...initialGlobalFilter_default, enabled: false };
            }

            return { ...prev, enabled: true };
        });
    };

    const handleGlobalInputChange = (inputKey, value) => {
        setGlobalInputStates(prev => ({ ...prev, [inputKey]: value }));
    };

    const handleGlobalKeyPress = (e, filterType, inputKey) => {
        if (e.key === 'Enter') { e.preventDefault(); handleGlobalChipAdd(filterType, inputKey); }
    };

    // ─── Manual tab handlers ──────────────────────────────────────────────────

    const manualAddChip = makeAddChip(setManualPairs, setManualInputStates, () => manualInputStates);
    const manualRemoveChip = makeRemoveChip(setManualPairs);
    const manualInputChange = makeHandleInputChange(setManualInputStates);
    const manualKeyPress = makeHandleKeyPress(manualAddChip);

    const handleManualPairChange = (index, field, value) => {
        setManualPairs(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
        if (field === 'sourceSchema' || field === 'targetSchema') {
            setErrors(prev => {
                const next = { ...prev };
                delete next[`${field}_${index}`];
                return next;
            });
        }
    };

    const handleManualRemovePair = (index) => {
        setManualPairs(prev => prev.filter((_, i) => i !== index));
        setManualInputStates(prev => {
            const next = { ...prev };
            ['includePrefix', 'includeSuffix', 'excludePrefix', 'excludeSuffix'].forEach(k => {
                delete next[`${index}-${k}`];
            });
            return next;
        });
        // Re-index error keys: remove errors for deleted pair, shift down errors for pairs after it
        setErrors(prev => {
            const next = {};
            Object.keys(prev).forEach(key => {
                const match = key.match(/^(sourceSchema_|targetSchema_|filter_)(\d+)$/);
                if (match) {
                    const i = parseInt(match[2], 10);
                    if (i === index) return; // drop errors for removed pair
                    const newKey = `${match[1]}${i > index ? i - 1 : i}`;
                    next[newKey] = prev[key];
                } else {
                    next[key] = prev[key];
                }
            });
            return next;
        });
    };

    const handleManualToggleFilter = (index) => {
        setManualPairs(prev => {
            const updated = [...prev];
            const enabling = !updated[index].addFilter;
            if (!enabling) {
                updated[index] = {
                    ...updated[index],
                    addFilter: false,
                    filterExpanded: false,
                    includePrefix: [],
                    includeSuffix: [],
                    excludePrefix: [],
                    excludeSuffix: [],
                };
                setManualInputStates(prev => {
                    const next = { ...prev };
                    ['includePrefix', 'includeSuffix', 'excludePrefix', 'excludeSuffix'].forEach(k => {
                        delete next[`${index}-${k}`];
                    });
                    return next;
                });
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`filter_${index}`];
                    return newErrors;
                });
            } else {
                updated[index] = { ...updated[index], addFilter: true, filterExpanded: true };
            }
            return updated;
        });
    };

    const handleAddManualPair = () => {
        setManualPairs(prev => [...prev, { ...initialSchemaPair }]);
    };

    // ─── Shared SchemaPair props builder ─────────────────────────────────────

    const sharedPairProps = (isAuto) => ({
        sourceSchemaOptions: schemas.source.map(s => ({ value: s, label: s })),
        targetSchemaOptions: schemas.target.map(s => ({ value: s, label: s })),
        loadingSchemas,
        schemasAvailable,
        connectionsReady,
        errors,
    });

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="schema-comparisons">
            {/* Tab row */}
            <div className="schema-tabs">
                <div className="schema-tab-group">
                    <label className={`schema-tab ${schemasAvailable && (activeTab === TABS.AUTOMATED) ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="schemaMode"
                            value={TABS.AUTOMATED}
                            checked={activeTab === TABS.AUTOMATED}
                            onChange={() => handleTabChange(TABS.AUTOMATED)}
                            disabled={!schemasAvailable}
                        />
                        All Schema (Automated)
                    </label>
                    <label className={`schema-tab ${schemasAvailable && (activeTab === TABS.MANUAL) ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="schemaMode"
                            value={TABS.MANUAL}
                            checked={activeTab === TABS.MANUAL}
                            onChange={() => handleTabChange(TABS.MANUAL)}
                            disabled={!schemasAvailable}
                        />
                        Manual Schema Mapping
                    </label>
                </div>
                {/* Global Add Filter — only in Automated tab */}
                {activeTab === TABS.AUTOMATED && (
                    <div className="global-filter-controls">
                        <label className="add-filter-checkbox">
                            <input
                                type="checkbox"
                                checked={schemasAvailable && globalFilter.enabled}
                                onChange={handleGlobalToggleFilter}
                                disabled={loadingSchemas || !schemasAvailable}
                            />
                            <span>Add Filter</span>
                        </label>
                        <Tooltip
                            text="Global Filter settings will overwrite filter settings across all schema pairs in this job"
                            className="global-filter"
                        >
                            <div className="info-icon-btn">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ width: '0.729vw', height: '0.729vw' }} /* 14px */
                                    viewBox="0 -960 960 960"
                                    fill="#ffffff"
                                >
                                    <path d="M423.5-703.5Q400-727 400-760t23.5-56.5Q447-840 480-840t56.5 23.5Q560-793 560-760t-23.5 56.5Q513-680 480-680t-56.5-23.5ZM420-120v-480h120v480H420Z" />
                                </svg>
                            </div>
                        </Tooltip>


                    </div>
                )}
            </div>

            {/* Global filter panel */}
            {activeTab === TABS.AUTOMATED && globalFilter.enabled && schemasAvailable && (
                <div className="global-filter-panel">
                    <div className="filter-tables">
                        <GlobalFilterTable
                            title="Include"
                            prefixChips={globalFilter.includePrefix}
                            suffixChips={globalFilter.includeSuffix}
                            inputStates={globalInputStates}
                            onAddChip={handleGlobalChipAdd}
                            onRemoveChip={handleGlobalChipRemove}
                            onInputChange={handleGlobalInputChange}
                            onKeyPress={handleGlobalKeyPress}
                            prefixKey="includePrefix"
                            suffixKey="includeSuffix"
                        />
                        <GlobalFilterTable
                            title="Exclude"
                            prefixChips={globalFilter.excludePrefix}
                            suffixChips={globalFilter.excludeSuffix}
                            inputStates={globalInputStates}
                            onAddChip={handleGlobalChipAdd}
                            onRemoveChip={handleGlobalChipRemove}
                            onInputChange={handleGlobalInputChange}
                            onKeyPress={handleGlobalKeyPress}
                            prefixKey="excludePrefix"
                            suffixKey="excludeSuffix"
                        />
                    </div>
                    {errors.globalFilter && (
                        <ErrorMessage message={errors.globalFilter} />
                    )}
                </div>
            )}

            {/* Automated tab content */}
            {activeTab === TABS.AUTOMATED && (
                <>
                    {autoPairs.map((pair, index) => (
                        <SchemaPair
                            key={index}
                            pair={pair}
                            index={index}
                            showRemove={autoPairs.length > 1}
                            {...sharedPairProps(true)}
                            inputStates={autoInputStates}
                            onRemove={handleAutoRemovePair}
                            onPairChange={handleAutoPairChange}
                            onToggleFilter={handleAutoToggleFilter}
                            onAddChip={autoAddChip}
                            onRemoveChip={autoRemoveChip}
                            onInputChange={autoInputChange}
                            onInputKeyPress={autoKeyPress}
                            modalBodyRef={modalBodyRef}
                        />
                    ))}
                </>
            )}

            {/* Manual tab content */}
            {activeTab === TABS.MANUAL && (
                <>
                    {manualPairs.map((pair, index) => (
                        <SchemaPair
                            key={index}
                            pair={pair}
                            index={index}
                            showRemove={manualPairs.length > 1}
                            {...sharedPairProps(false)}
                            inputStates={manualInputStates}
                            onRemove={handleManualRemovePair}
                            onPairChange={handleManualPairChange}
                            onToggleFilter={handleManualToggleFilter}
                            onAddChip={manualAddChip}
                            onRemoveChip={manualRemoveChip}
                            onInputChange={manualInputChange}
                            onInputKeyPress={manualKeyPress}
                            modalBodyRef={modalBodyRef}
                        />
                    ))}
                    <div className="add-schema-pair-btn-container">
                        <button
                            type="button"
                            className="add-schema-pair-btn"
                            onClick={handleAddManualPair}
                            disabled={loadingSchemas || !schemasAvailable}
                        >
                            <Plus style={{ width: '0.833vw', height: '0.833vw' }} />
                            Add Schema Pair
                        </button>
                    </div>
                </>
            )}

            <ErrorMessage message={errors.schemaPairs} />
        </div>
    );
};

// ─── Global filter table (Include / Exclude) ──────────────────────────────────

const GlobalFilterTable = ({
    title,
    prefixChips,
    suffixChips,
    inputStates,
    onAddChip,
    onRemoveChip,
    onInputChange,
    onKeyPress,
    prefixKey,
    suffixKey,
}) => {
    const prefixInputKey = `global-${prefixKey}`;
    const suffixInputKey = `global-${suffixKey}`;

    return (
        <div className="filter-table">
            <h4>{title}</h4>
            <div className="filter-table-content">
                <div className="filter-row">
                    <GlobalFilterCell
                        label="Prefix"
                        inputKey={prefixInputKey}
                        chips={prefixChips}
                        inputValue={inputStates[prefixInputKey] || ''}
                        onAdd={() => onAddChip(prefixKey, prefixInputKey)}
                        onRemove={(i) => onRemoveChip(prefixKey, i)}
                        onInputChange={(val) => onInputChange(prefixInputKey, val)}
                        onKeyPress={(e) => onKeyPress(e, prefixKey, prefixInputKey)}
                    />
                    <div className="filter-divider"></div>
                    <GlobalFilterCell
                        label="Suffix"
                        inputKey={suffixInputKey}
                        chips={suffixChips}
                        inputValue={inputStates[suffixInputKey] || ''}
                        onAdd={() => onAddChip(suffixKey, suffixInputKey)}
                        onRemove={(i) => onRemoveChip(suffixKey, i)}
                        onInputChange={(val) => onInputChange(suffixInputKey, val)}
                        onKeyPress={(e) => onKeyPress(e, suffixKey, suffixInputKey)}
                    />
                </div>
            </div>
        </div>
    );
};

const GlobalFilterCell = ({ label, inputKey, chips, inputValue, onAdd, onRemove, onInputChange, onKeyPress }) => (
    <div className="filter-cell">
        <label>{label}</label>
        <div className="filter-input-with-icon">
            <input
                type="text"
                placeholder={`Enter ${label}`}
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={onKeyPress}
            />
            <button type="button" className="icon-in-input" onClick={onAdd}>
                <CircleArrowRight style={{ width: '1.042vw', height: '1.042vw' }} color="#2563eb" />
            </button>
        </div>
        <div className="chips-container">
            {chips.map((chip, i) => (
                <div key={i} className="chip">
                    <span>{chip}</span>
                    <button type="button" onClick={() => onRemove(i)}>
                        <X style={{ width: '0.729vw', height: '0.729vw' }} />
                    </button>
                </div>
            ))}
        </div>
    </div>
);

export default SchemaComparisons;